// src/pages/AdminDashboard.tsx
import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useAuth } from '../context/AuthContext';
import { getUsers, getTasks, createTask, updateTask, updateUserRole, User, Task } from '../services/mockApi';
import { Container, FlexCenter } from '../styles/commonStyles';
import { Button } from '../components/Button';
import { theme } from '../styles/theme';

const DashboardWrapper = styled.div`
  display: flex;
  min-height: 100vh;
  background-color: ${({ theme }) => theme.colors.background};
`;

const Sidebar = styled.nav<{ isOpen: boolean }>`
  width: 250px;
  background-color: ${({ theme }) => theme.colors.primary};
  color: white;
  padding: ${({ theme }) => theme.spacing.large};
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.medium};
  height: 100vh;
  z-index: 1000;
  transition: transform 0.3s ease-in-out;

  position: fixed;
  transform: ${({ isOpen }) => (isOpen ? 'translateX(0)' : 'translateX(-100%)')};

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    width: 200px;
  }

  @media (min-width: ${({ theme }) => theme.breakpoints.tablet}) {
    position: sticky;
    top: 0;
    transform: translateX(0);
    align-self: start;
  }
`;

const SidebarToggle = styled.button`
  display: none;
  position: fixed;
  top: 16px;
  left: 16px;
  z-index: 1100;
  background-color: ${({ theme }) => theme.colors.primary};
  color: white;
  border: none;
  padding: 8px 12px;
  border-radius: 8px;
  cursor: pointer;

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    display: block;
  }
`;

const SidebarItem = styled.div<{ active: boolean }>`
  padding: 12px 16px;
  border-radius: 8px;
  cursor: pointer;
  background-color: ${({ active, theme }) =>
    active ? 'rgba(255, 255, 255, 0.2)' : 'transparent'};
  transition: background-color 0.3s ease;
  &:hover {
    background-color: rgba(255, 255, 255, 0.1);
  }
`;

const MainContent = styled(Container)`
  flex: 1;
  padding: ${({ theme }) => theme.spacing.large};
  background-color: ${({ theme }) => theme.colors.backgroundLight};
  margin-left: 250px;

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    margin-left: 0;
    padding: ${({ theme }) => theme.spacing.medium};
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    padding: ${({ theme }) => theme.spacing.small};
  }
`;

const Title = styled.h2`
  font-size: 2rem;
  color: ${({ theme }) => theme.colors.primary};
  margin-bottom: ${({ theme }) => theme.spacing.medium};

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    font-size: 1.6rem;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    font-size: 1.4rem;
  }
`;

const Section = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing.large};
  background: white;
  border-radius: 12px;
  padding: ${({ theme }) => theme.spacing.medium};
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    padding: ${({ theme }) => theme.spacing.small};
  }
`;

const SectionTitle = styled.h3`
  font-size: 1.5rem;
  color: ${({ theme }) => theme.colors.primary};
  margin-bottom: ${({ theme }) => theme.spacing.medium};

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    font-size: 1.3rem;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    font-size: 1.2rem;
  }
`;

const UserCard = styled.div`
  background: white;
  padding: ${({ theme }) => theme.spacing.medium};
  border-radius: 8px;
  margin-bottom: ${({ theme }) => theme.spacing.small};
  display: flex;
  justify-content: space-between;
  align-items: center;
  border: 1px solid ${({ theme }) => theme.colors.muted};
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  color: ${({ theme }) => theme.colors.primary};


  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    flex-direction: column;
    align-items: flex-start;
    gap: 10px;
  }
`;

const TaskCard = styled(UserCard)`
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 12px rgba(0, 0, 0, 0.1);
  }
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.medium};
  background: white;
  padding: ${({ theme }) => theme.spacing.medium};
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
  margin-bottom: ${({ theme }) => theme.spacing.large};
`;

const Input = styled.input`
  padding: 10px;
  border: 1px solid ${({ theme }) => theme.colors.muted};
  border-radius: 5px;
  font-size: 1rem;

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
  }
`;

const Select = styled.select`
  padding: 8px;
  border: 1px solid ${({ theme }) => theme.colors.muted};
  border-radius: 5px;
  font-size: 1rem;
  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
  }
`;

const NotificationBanner = styled.div`
  position: fixed;
  top: 20px;
  right: 20px;
  background-color: ${({ theme }) => theme.colors.primary};
  color: white;
  padding: 12px 20px;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  display: flex;
  align-items: center;
  gap: 10px;
  z-index: 1000;
  max-width: 400px;

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    top: 60px;
    right: 10px;
    left: 10px;
    max-width: calc(100% - 20px);
    font-size: 0.9rem;
  }
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: white;
  font-size: 1.2rem;
  cursor: pointer;
`;

const NoUsersMessage = styled.p`
  color: ${({ theme }) => theme.colors.text};
`;

const AdminDashboard: React.FC = () => {
  const { user, logout } = useAuth();
  const [users, setUsers] = useState<User[]>([]);
  const [tasks, setTasks] = useState<Task[]>([]);
  const [notifications, setNotifications] = useState<string[]>([]);
  const [isSidebarOpen, setIsSidebarOpen] = useState(window.innerWidth >= 768);
  const [activeTab, setActiveTab] = useState('users');
  const [newTask, setNewTask] = useState({
    title: '',
    description: '',
    assignedTo: '',
    departmentId: '1', // Default to department 1 for simplicity
    createdBy: user?.id || '1',
    dueDate: '',
    assignedBy: user?.id || '1',
  });

  useEffect(() => {
    const handleResize = () => {
      setIsSidebarOpen(window.innerWidth >= 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      const fetchedUsers = await getUsers();
      const fetchedTasks = await getTasks();
      setUsers(fetchedUsers);
      setTasks(fetchedTasks);
      // Set default assignedTo to the first employee/manager if available
      const firstEmployee = fetchedUsers.find((u: User) => u.role !== 'admin');
      if (firstEmployee) {
        setNewTask((prev) => ({ ...prev, assignedTo: firstEmployee.id }));
      }
      // Notify that signups are disabled
      addNotification('Signups are disabled. Only JSONPlaceholder users are available.');
    };
    fetchData();
  }, []);

  const addNotification = (message: string) => {
    setNotifications((prev) => [...prev, message]);
  };

  const removeNotification = (index: number) => {
    setNotifications((prev) => prev.filter((_, i) => i !== index));
  };

  const handleAddTask = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const task = await createTask(
        newTask.title,
        newTask.description,
        newTask.assignedTo,
        newTask.departmentId,
        newTask.createdBy,
        newTask.dueDate,
        newTask.assignedBy
      );
      setTasks([...tasks, task]);
      addNotification('Task added successfully!');
      setNewTask({
        title: '',
        description: '',
        assignedTo: newTask.assignedTo,
        departmentId: '1',
        createdBy: user?.id || '1',
        dueDate: '',
        assignedBy: user?.id || '1',
      });
    } catch (error) {
      addNotification('Failed to add task.');
    }
  };

  const handleUpdateTaskStatus = async (taskId: string, newStatus: Task['status']) => {
    const task = tasks.find((t) => t.id === taskId);
    if (!task) return;

    const updatedTask = await updateTask(taskId, { ...task, status: newStatus });
    setTasks(tasks.map((t) => (t.id === taskId ? updatedTask : t)));
    addNotification(`Task "${updatedTask.title}" updated to "${newStatus}".`);
  };

  const handleUpdateUserRole = async (userId: string, newRole: 'employee' | 'manager') => {
    try {
      const updatedUser = await updateUserRole(userId, newRole);
      setUsers(users.map((u) => (u.id === userId ? updatedUser : u)));
      addNotification(`User role updated to "${newRole}".`);
    } catch (error) {
      addNotification('Failed to update user role.');
    }
  };

  return (
    <DashboardWrapper>
      <SidebarToggle onClick={() => setIsSidebarOpen(!isSidebarOpen)}>
        {isSidebarOpen ? '✕' : '☰'}
      </SidebarToggle>

      <Sidebar isOpen={isSidebarOpen}>
        <Title style={{ color: 'white', fontSize: '1.8rem' }}>Admin Portal</Title>
        <SidebarItem
          active={activeTab === 'users'}
          onClick={() => {
            setActiveTab('users');
            setIsSidebarOpen(false);
          }}
        >
          Manage Users
        </SidebarItem>
        <SidebarItem
          active={activeTab === 'tasks'}
          onClick={() => {
            setActiveTab('tasks');
            setIsSidebarOpen(false);
          }}
        >
          Manage Tasks
        </SidebarItem>
        <SidebarItem
          active={false}
          onClick={() => {
            logout();
            setIsSidebarOpen(false);
          }}
        >
          Log Out
        </SidebarItem>
      </Sidebar>

      <MainContent>
        <Title>Admin Dashboard</Title>

        {notifications.map((notification, index) => (
          <NotificationBanner key={index}>
            <span>{notification}</span>
            <CloseButton onClick={() => removeNotification(index)}>×</CloseButton>
          </NotificationBanner>
        ))}

        {activeTab === 'users' && (
          <Section>
            <SectionTitle>Manage Users</SectionTitle>
            {users.length === 0 ? (
              <NoUsersMessage>No users available.</NoUsersMessage>
            ) : (
              users
                .filter((u) => u.id !== user?.id) // Exclude the current admin user
                .map((u) => (
                  <UserCard key={u.id}>
                    <div style={{ flex: 1 }}>
                      <strong>{u.username}</strong>
                      <div>Password: {u.password}</div>
                      <div>Role: {u.role}</div>
                    </div>
                    <Select
                      value={u.role}
                      onChange={(e) =>
                        handleUpdateUserRole(u.id, e.target.value as 'employee' | 'manager')
                      }
                    >
                      <option value="employee">Employee</option>
                      <option value="manager">Manager</option>
                    </Select>
                  </UserCard>
                ))
            )}
          </Section>
        )}

        {activeTab === 'tasks' && (
          <>
            <Form onSubmit={handleAddTask}>
              <SectionTitle>Add New Task</SectionTitle>
              <Input
                type="text"
                placeholder="Task Title"
                value={newTask.title}
                onChange={(e) => setNewTask({ ...newTask, title: e.target.value })}
                required
              />
              <Input
                type="text"
                placeholder="Description"
                value={newTask.description}
                onChange={(e) => setNewTask({ ...newTask, description: e.target.value })}
                required
              />
              <Select
                value={newTask.assignedTo}
                onChange={(e) => setNewTask({ ...newTask, assignedTo: e.target.value })}
                required
              >
                <option value="">Select User</option>
                {users
                  .filter((u) => u.role !== 'admin')
                  .map((u) => (
                    <option key={u.id} value={u.id}>
                      {u.username} ({u.role})
                    </option>
                  ))}
              </Select>
              <Input
                type="date"
                value={newTask.dueDate}
                onChange={(e) => setNewTask({ ...newTask, dueDate: e.target.value })}
                required
              />
              <Button as="button" type="submit">
                Add Task
              </Button>
            </Form>

            <Section>
              <SectionTitle>All Tasks</SectionTitle>
              {tasks.length === 0 ? (
                <p style={{ color: theme.colors.text }}>No tasks available.</p>
              ) : (
                tasks.map((task) => (
                  <TaskCard key={task.id}>
                    <div style={{ flex: 1 }}>
                      <strong>{task.title}</strong> - {task.description}
                      <div>Assigned to: {users.find((u) => u.id === task.assignedTo)?.username}</div>
                      <div>Due: {task.dueDate}</div>
                      <div>Status: {task.status}</div>
                    </div>
                    <Select
                      value={task.status}
                      onChange={(e) =>
                        handleUpdateTaskStatus(task.id, e.target.value as Task['status'])
                      }
                    >
                      <option value="pending">Pending</option>
                      <option value="in-progress">In Progress</option>
                      <option value="completed">Completed</option>
                    </Select>
                  </TaskCard>
                ))
              )}
            </Section>
          </>
        )}
      </MainContent>
    </DashboardWrapper>
  );
};

export default AdminDashboard;